import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button, Card, Badge, LoadingState, ErrorState, EmptyState, GradientBar } from "@/components/design-system";
import { getOwnCampaign, openCampaign, closeCampaign, listSubmissions, toggleSubmissionHelpful } from "@/lib/campaigns";
import { formatApiError } from "@/lib/api";
import AIReportPanel from "@/components/AIReportPanel";

function StatusBadge({ status }) {
  if (status === "open") return <Badge variant="active">Open</Badge>;
  if (status === "closed") return <Badge variant="neutral">Closed</Badge>;
  return <Badge variant="neutral">Draft</Badge>;
}

function AnswerBlock({ a }) {
  const isEmpty = a.value === null || a.value === undefined || a.value === "";
  return (
    <div className="border-t border-[#0B1F3A]/10 pt-3 mt-3 first:mt-0 first:pt-0 first:border-0">
      <div className="text-xs font-bold uppercase tracking-widest text-[#0B1F3A]/50">{a.question_type}</div>
      <div className="text-sm font-semibold text-[#0B1F3A] mt-1">{a.question_text}</div>
      {isEmpty ? (
        <div className="text-sm text-[#0B1F3A]/50 italic mt-1">— skipped —</div>
      ) : a.question_type === "likert5" ? (
        <div className="mt-2 flex items-center gap-1.5">
          {[1,2,3,4,5].map((n) => (
            <span key={n} className={n <= parseInt(a.value, 10) ? "w-6 h-2 rounded bg-[#2D7FF9]" : "w-6 h-2 rounded bg-[#E8F1FF]"} />
          ))}
          <span className="ml-2 text-sm font-bold text-[#0B1F3A]">{a.value}/5</span>
        </div>
      ) : (
        <div className="text-sm text-[#0B1F3A]/85 mt-1 whitespace-pre-wrap">{String(a.value)}</div>
      )}
    </div>
  );
}

export default function CampaignDetail() {
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [subs, setSubs] = useState([]);
  const [state, setState] = useState({ status: "loading", err: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setState({ status: "loading", err: "" });
    try {
      const c = await getOwnCampaign(id);
      setCampaign(c);
      const s = await listSubmissions(id);
      setSubs(s);
      setState({ status: "ready", err: "" });
    } catch (err) { setState({ status: "error", err: formatApiError(err) }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const onOpen = async () => { setBusy(true); try { setCampaign(await openCampaign(id)); } catch (e) { alert(formatApiError(e)); } finally { setBusy(false); } };
  const onClose = async () => { setBusy(true); try { setCampaign(await closeCampaign(id)); } catch (e) { alert(formatApiError(e)); } finally { setBusy(false); } };
  const onHelpful = async (sid) => { try { const next = await toggleSubmissionHelpful(id, sid); setSubs((xs) => xs.map((s) => s.id === next.id ? next : s)); } catch (e) { alert(formatApiError(e)); } };

  if (state.status === "loading") return <LoadingState label="Loading campaign…" />;
  if (state.status === "error") return <ErrorState detail={state.err} action={<Button variant="secondary" onClick={load}>Retry</Button>} />;
  if (!campaign) return null;

  const shareUrl = `${window.location.origin}/campaign/${campaign.id}`;
  const pct = campaign.target_responses ? Math.min(100, Math.round((campaign.response_count / campaign.target_responses) * 100)) : null;

  return (
    <div data-testid="campaign-detail-page">
      <div className="mb-6"><Link to="/app/campaigns" className="text-sm text-[#2D7FF9] font-medium">← Back to campaigns</Link></div>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={campaign.status} />
            <span className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60">{campaign.template_name}</span>
          </div>
          <h1 data-testid="campaign-title" className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">{campaign.name}</h1>
          <p className="text-sm text-[#0B1F3A]/70 mt-2">On <Link className="text-[#2D7FF9] font-semibold" to={`/p/${campaign.product_slug}`}>{campaign.product_name}</Link></p>
          {campaign.description && <p className="mt-4 text-[#0B1F3A]/80 max-w-3xl leading-relaxed">{campaign.description}</p>}
        </div>
        <div className="text-right shrink-0">
          <div className="font-[Montserrat] font-extrabold text-4xl text-[#0B1F3A]">{campaign.response_count}</div>
          <div className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/60">responses{campaign.target_responses ? ` / ${campaign.target_responses}` : ""}</div>
        </div>
      </div>
      {pct !== null && (
        <div className="mt-4 h-2 rounded-full bg-[#E8F1FF] overflow-hidden max-w-xl">
          <div className="h-full bg-[#2D7FF9]" style={{ width: `${pct}%` }} />
        </div>
      )}

      <Card className="mt-8">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div>
            <h2 className="font-[Montserrat] font-bold text-lg text-[#0B1F3A]">Lifecycle</h2>
            <p className="text-sm text-[#0B1F3A]/70 mt-1">
              {campaign.status === "draft" && "Draft. Launch when you're ready to open it to the community."}
              {campaign.status === "open" && "Live. Community members can submit feedback now."}
              {campaign.status === "closed" && "Closed. No new submissions can be added."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {campaign.status === "draft" && (
              <Button data-testid="campaign-launch" variant="primary" onClick={onOpen} disabled={busy}>Launch campaign</Button>
            )}
            {campaign.status === "open" && (
              <>
                <button type="button" onClick={() => navigator.clipboard?.writeText(shareUrl)} data-testid="campaign-copy-link" className="px-6 py-3 rounded-lg font-semibold border-2 border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white transition-colors">Copy share link</button>
                <Button data-testid="campaign-close" variant="ghost" onClick={onClose} disabled={busy}>Close campaign</Button>
              </>
            )}
          </div>
        </div>
      </Card>

      <div className="mt-8">
        <AIReportPanel campaignId={campaign.id} />
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 !p-0 overflow-hidden">
          <div className="p-6 md:p-8">
            <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">Submissions</h2>
            <p className="text-sm text-[#0B1F3A]/70 mt-1">Toggle "Helpful" so the community knows which answers moved the product forward.</p>
          </div>
          <GradientBar />
          <div className="p-6 md:p-8">
            {subs.length === 0 ? (
              <EmptyState
                title="No submissions yet"
                description={campaign.status === "open" ? "Once reviewers submit, their structured answers will appear here." : "This campaign hasn't been launched yet. Launch it to start collecting feedback."}
              />
            ) : (
              <div className="space-y-6" data-testid="campaign-submissions">
                {subs.map((s) => (
                  <Card key={s.id} data-testid={`submission-${s.id}`} className="!p-6 !shadow-none border-[#0B1F3A]/15">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-[#0B1F3A]">{s.reviewer_name}</div>
                        <div className="text-xs text-[#0B1F3A]/60">{new Date(s.created_at).toLocaleString()}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        {s.moderation_status === "hidden" && <Badge variant="admin">Hidden by admin</Badge>}
                        <button
                          type="button"
                          onClick={() => onHelpful(s.id)}
                          data-testid={`submission-helpful-${s.id}`}
                          className={"px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border " + (s.helpful ? "bg-[#0B1F3A] text-white border-[#0B1F3A]" : "bg-white text-[#0B1F3A]/70 border-[#0B1F3A]/20 hover:border-[#0B1F3A]/40")}
                        >{s.helpful ? "★ Helpful" : "Mark helpful"}</button>
                      </div>
                    </div>
                    <div className="mt-4">
                      {s.answers.map((a) => <AnswerBlock key={a.question_id} a={a} />)}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </Card>

        <Card>
          <h3 className="font-[Montserrat] font-bold text-lg text-[#0B1F3A]">Question set</h3>
          <ol className="mt-3 space-y-3 text-sm">
            {campaign.questions.map((q, i) => (
              <li key={q.id} className="flex gap-3 text-[#0B1F3A]/85">
                <span className="font-bold text-[#2D7FF9] w-6 shrink-0">{i+1}.</span>
                <div>
                  <div>{q.text}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/50 mt-1">{q.type}{q.required ? " · required" : " · optional"}</div>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-6 pt-6 border-t border-[#0B1F3A]/10 text-xs text-[#0B1F3A]/60 space-y-1">
            <div>Template: {campaign.template_name} · v{campaign.template_version}</div>
            <div>Created {campaign.created_at ? new Date(campaign.created_at).toLocaleDateString() : "—"}</div>
            {campaign.opened_at && <div>Launched {new Date(campaign.opened_at).toLocaleDateString()}</div>}
            {campaign.closed_at && <div>Closed {new Date(campaign.closed_at).toLocaleDateString()}</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
